import { Component, type ErrorInfo, type ReactNode } from 'react';
import { brand } from '@iheart/shared';

interface Props {
  children: ReactNode;
  /** Optional fallback to render instead of the default error card */
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  isChunkError: boolean;
}

/**
 * Global error boundary for the app.
 *
 * Handles two common failure modes:
 * 1. **Chunk load errors** — after a deploy, old chunk URLs 404. The
 *    boundary detects these and offers a one-click page reload.
 * 2. **Render crashes** — unexpected JS errors in components. Shows a
 *    friendly card instead of a white screen.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, isChunkError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    const isChunkError =
      error.message.includes('Failed to fetch dynamically imported module') ||
      error.message.includes('Loading chunk') ||
      error.message.includes('Loading CSS chunk') ||
      error.name === 'ChunkLoadError';

    return { hasError: true, error, isChunkError };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // Log to console in dev; in prod this would go to a service like Sentry
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  override render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    if (this.state.isChunkError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-neutral-50 p-6">
          <div className="max-w-sm text-center">
            <div
              className="mx-auto mb-4 flex size-14 items-center justify-center rounded-2xl text-white"
              style={{ backgroundImage: brand.gradient.primary }}
            >
              <svg
                className="size-7"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-neutral-800">
              New version available
            </h2>
            <p className="mt-2 text-sm text-neutral-500">
              iHeart CNTRL has been updated. Reload the page to get the latest
              version.
            </p>
            <button
              type="button"
              onClick={this.handleReload}
              className="mt-4 rounded-xl px-6 py-2.5 text-sm font-medium text-white transition-transform hover:scale-105"
              style={{ backgroundImage: brand.gradient.primary }}
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-50 p-6">
        <div className="max-w-md text-center">
          <div className="mx-auto mb-4 flex size-14 items-center justify-center rounded-2xl bg-red-50 text-red-500">
            <svg
              className="size-7"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
              />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-neutral-800">
            Something went wrong
          </h2>
          <p className="mt-2 text-sm text-neutral-500">
            An unexpected error occurred. Try reloading the page or going back
            to the dashboard.
          </p>
          {this.state.error && (
            <details className="mx-auto mt-3 max-w-xs text-left">
              <summary className="cursor-pointer text-xs text-neutral-400 hover:text-neutral-600">
                Error details
              </summary>
              <pre className="mt-1 overflow-x-auto rounded bg-neutral-100 p-2 text-[10px] text-neutral-600">
                {this.state.error.message}
              </pre>
            </details>
          )}
          <div className="mt-4 flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={this.handleReload}
              className="rounded-xl px-5 py-2 text-sm font-medium text-white transition-transform hover:scale-105"
              style={{ backgroundImage: brand.gradient.primary }}
            >
              Reload page
            </button>
            <button
              type="button"
              onClick={this.handleGoHome}
              className="rounded-xl border border-neutral-200 bg-white px-5 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
            >
              Go to dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }
}
